import { createContext, useContext, useState } from 'react';

import { useSessionStorage } from '@betterment/js-runtime';
import { type InferType, ObjectSchema } from 'yup';

/**
 * Flow config types
 *
 * AnyStepConfig -- type for step in config object
 * AnyFlowConfig -- type for whole config object
 * HasOnlyAllowedKeys -- type for validating step in config object
 * HasValidDependencies -- type for validating dependencies config object
 * ValidateFlowConfig -- type for validating config object
 */
type AnyStepConfig = {
  schema: ObjectSchema<Record<string, unknown>>;
  dependencies: ObjectSchema<Record<string, Record<string, unknown>>>;
};
type AnyFlowConfig = Record<string, AnyStepConfig>;
type HasOnlyAllowedKeys<StepConfig> = keyof StepConfig extends keyof AnyStepConfig
  ? StepConfig
  : {
      error: 'Invalid key in step config';
      invalidKeys: Exclude<keyof StepConfig, keyof AnyStepConfig>;
    };
type HasValidDependencies<FlowConfig, StepConfig> = StepConfig extends {
  dependencies: ObjectSchema<infer DependencyConfig>;
}
  ? keyof DependencyConfig extends keyof FlowConfig
    ? StepConfig
    : { error: 'Invalid dependency key'; invalidKeys: Exclude<keyof DependencyConfig, keyof FlowConfig> }
  : never;
type ValidateFlowConfig<FlowConfig extends AnyFlowConfig> = {
  [Step in keyof FlowConfig]: HasOnlyAllowedKeys<FlowConfig[Step]> extends FlowConfig[Step]
    ? HasValidDependencies<FlowConfig, FlowConfig[Step]>
    : HasOnlyAllowedKeys<FlowConfig[Step]>;
};

/**
 * useStep types
 *
 * Values -- type for values per step
 * Dependencies -- type for dependencies per step
 * AnyStep -- type for step config
 * UseStep -- type for useStep return
 */
type Values<FlowConfig extends AnyFlowConfig> = {
  [Step in keyof FlowConfig]: InferType<FlowConfig[Step]['schema']>;
};
type Dependencies<FlowConfig extends AnyFlowConfig> = {
  [Step in keyof FlowConfig]: InferType<FlowConfig[Step]['dependencies']>;
};
type AnyStep<FlowConfig extends AnyFlowConfig> = keyof FlowConfig;
type UseStep<FlowConfig extends AnyFlowConfig, Step extends AnyStep<FlowConfig>> = {
  values: Values<FlowConfig>[Step] | null;
  dependencies: Dependencies<FlowConfig>[Step];
  commit: (values: Values<FlowConfig>[Step]) => void;
};

/**
 * Type utils
 *
 * StepValues -- type for inferring type per given step
 */
export type StepValues<FlowConfig extends AnyFlowConfig, Step extends AnyStep<FlowConfig>> = InferType<
  FlowConfig[Step]['schema']
>;

/**
 * createConfig
 * @param config
 *
 * util for creating a flow config object that provides type safety and auto-complete
 */
export const createConfig = <const Config extends AnyFlowConfig>(config: Config & ValidateFlowConfig<Config>): Config =>
  config;

/**
 * createFlowContext
 * @param flowConfig
 *
 * factory method to create a typed useStep hook and FlowProvider
 */
export const createFlowContext = <FlowConfig extends AnyFlowConfig>(flowConfig: FlowConfig) => {
  const useFlowStorage = (
    flowId: string
  ): [Partial<Values<FlowConfig>>, (newFlowValues: Partial<Values<FlowConfig>>) => void] => {
    const { values, saveValues } = useSessionStorage<Partial<Values<FlowConfig>>>(`bmt-flow-${flowId}`, {});
    const [flowValues, setFlowValues] = useState<Partial<Values<FlowConfig>>>(values ?? {});

    const saveFlowValues = (newFlowValues: Partial<Values<FlowConfig>>) => {
      setFlowValues(newFlowValues);
      saveValues(newFlowValues);
    };

    return [flowValues, saveFlowValues];
  };

  const FlowContext = createContext<{
    flowValues: Partial<Values<FlowConfig>>;
    saveFlowValues: (values: Partial<Values<FlowConfig>>) => void;
  } | null>(null);

  const FlowProvider: React.FC<React.PropsWithChildren<{ flowId: string }>> = ({ children, flowId }) => {
    const [flowValues, saveFlowValues] = useFlowStorage(flowId);

    return (
      <FlowContext.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={{
          flowValues,
          saveFlowValues,
        }}
      >
        {children}
      </FlowContext.Provider>
    );
  };

  const useFlow = () => {
    const context = useContext(FlowContext);
    if (!context) {
      throw new Error('useFlow must be used within FlowProvider');
    }

    return context;
  };

  const useStep = <Step extends AnyStep<FlowConfig>>(stepName: Step): UseStep<FlowConfig, Step> => {
    const { flowValues, saveFlowValues } = useFlow();

    const commit = (stepValues: Values<FlowConfig>[Step]) => {
      try {
        flowConfig[stepName]!.schema.validateSync(stepValues);
      } catch (e) {
        throw new Error('Step values did not pass validation', { cause: e });
      }

      const updatedFlowValues = { ...flowValues, [stepName]: stepValues };
      saveFlowValues(updatedFlowValues);
    };

    const getStepValues = () => {
      try {
        return flowConfig[stepName]!.schema.validateSync(flowValues[stepName]);
      } catch {
        return null;
      }
    };

    const getDependencies = () => {
      try {
        return flowConfig[stepName]!.dependencies.validateSync(flowValues);
      } catch (e) {
        throw new Error('Step does not meet dependency requirements', { cause: e });
      }
    };

    return {
      commit,
      values: getStepValues(),
      dependencies: getDependencies(),
    };
  };

  return {
    FlowProvider,
    useStep,
  };
};
